import { dateTimeFormat } from "GenericForms/Helper";
import React, { useState } from "react";
import {
  Table,
  Input,
  Label,
  Modal,
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useFormik } from "formik";
import apiGlobal from "global/api.global";
import { errorToast, successToast } from "Components/Toasts";

const euaVoyageEventData = [
  {
    type: "voyage",
    id: 101,
    name: "V001 - Rotterdam to Hamburg",
    from_port: "Rotterdam",
    to_port: "Hamburg",
    cargo_operation: false,
    reason: null as string | null,
    events: [
      { name: "Ballast Start", date: "2025-01-03T08:15Z", applicable: false },
      { name: "At Sea", date: "2025-01-04T10:00Z", applicable: false },
      { name: "Approaching Port", date: "2025-01-05T05:30Z", applicable: false },
    ],
  },
  {
    type: "port",
    id: 2,
    name: "Hamburg",
    from_port: null as string | null,
    to_port: "Hamburg",
    cargo_operation: true,
    reason: null as string | null,
    events: [
      { name: "Arrival", date: "2025-01-05T06:00Z", applicable: true },
      { name: "Cargo Operation Start", date: "2025-01-05T08:00Z", applicable: true },
      { name: "Cargo Operation End", date: "2025-01-06T12:00Z", applicable: true },
      { name: "Departure", date: "2025-01-07T10:00Z", applicable: false },
    ],
  },
  {
    type: "voyage",
    id: 102,
    name: "V002 - Hamburg to Genoa",
    from_port: "Hamburg",
    to_port: "Genoa",
    cargo_operation: false,
    reason: null as string | null,
    events: [
      { name: "Departure", date: "2025-01-08T06:00Z", applicable: false },
      { name: "At Sea", date: "2025-01-09T12:00Z", applicable: false },
      { name: "Approaching Port", date: "2025-01-11T09:00Z", applicable: false },
    ],
  },
  {
    type: "port",
    id: 3,
    name: "Genoa",
    from_port: null as string | null,
    to_port: "Genoa",
    cargo_operation: true,
    reason: null as string | null,
    events: [
      { name: "Arrival", date: "2025-01-11T11:00Z", applicable: true },
      { name: "Cargo Operation Start", date: "2025-01-11T13:00Z", applicable: false },
      { name: "Cargo Operation End", date: "2025-01-12T22:00Z", applicable: false },
      { name: "Departure", date: "2025-01-13T07:00Z", applicable: true },
    ],
  },
];

const EuaVoyageTable: React.FC = () => {
  const [reasonPopup, setReasonPopup] = useState(false);
  const [selectedRowIdx, setSelectedRowIdx] = useState<number | null>(null);
  const [tempReason, setTempReason] = useState("");

  const formik = useFormik({
    initialValues: {
      voyages: euaVoyageEventData,
      showReasonLink: {} as Record<number, boolean>,
    },
    onSubmit: async (values) => {
      const transformed = values.voyages.reduce((acc: any, current: any, idx: number) => {
        const originalType = euaVoyageEventData[idx].type;
        if (originalType === "port" && current.type === "voyage") {
          const prev = acc[acc.length - 1];
          if (prev && prev.type === "voyage") {
            prev.events = [...prev.events, ...current.events];
          }
          return acc;
        }
        acc.push(current);
        return acc;
      }, [] as typeof values.voyages);

      try {
        const response = await apiGlobal.post("/generate_eua_analysis", {
          voyages: transformed
        });

        if (response.status === 200) {
          successToast("EUA Analysis generated successfully!");
        } else {
          errorToast("Failed to generate EUA Analysis");
        }
      } catch (err) {
        console.error("API ERROR:", err);
        errorToast("Failed to generate EUA Analysis");
      }
    }
  });

  // Type change handler
  const handleTypeChange = (idx: number, newType: "voyage" | "port") => {
    const updated = [...formik.values.voyages];
    const originalType = euaVoyageEventData[idx].type;
    const updatedEvents =
      newType === "port"
        ? updated[idx].events.map((e) => ({ ...e, applicable: true }))
        : updated[idx].events.map((e) => ({ ...e, applicable: false }));
    updated[idx] = { ...updated[idx], type: newType, events: updatedEvents };
    const newShowReason = { ...formik.values.showReasonLink };
    newShowReason[idx] = newType !== originalType;
    formik.setValues({ ...formik.values, voyages: updated, showReasonLink: newShowReason });
  };

  // Checkbox handler
  const handleEventCheckboxChange = (
    rowIdx: number,
    eventIdx: number,
    checked: boolean
  ) => {
    const updated = [...formik.values.voyages];
    updated[rowIdx].events[eventIdx].applicable = checked;
    formik.setFieldValue("voyages", updated);
  };

  // Move event UP to previous row
  const handleMoveUp = (rowIdx: number, eventIdx: number) => {
    if (rowIdx === 0) return;
    const updated = [...formik.values.voyages];
    const [movedEvent] = updated[rowIdx].events.splice(eventIdx, 1);
    movedEvent.applicable = false;
    updated[rowIdx - 1].events.push(movedEvent);
    formik.setFieldValue("voyages", updated);
  };

  // Move event DOWN to next row
  const handleMoveDown = (rowIdx: number, eventIdx: number) => {
    if (rowIdx === formik.values.voyages.length - 1) return;
    const updated = [...formik.values.voyages];
    const [movedEvent] = updated[rowIdx].events.splice(eventIdx, 1);
    movedEvent.applicable = false;
    updated[rowIdx + 1].events.unshift(movedEvent);
    formik.setFieldValue("voyages", updated);
  };

  // Reason modal handlers
  const handleOpenReasonModal = (idx: number) => {
    setSelectedRowIdx(idx);
    setTempReason(formik.values.voyages[idx].reason || "");
    setReasonPopup(true);
  };

  // Save reason from modal
  const handleSaveReason = () => {
    if (selectedRowIdx !== null) {
      const updated = [...formik.values.voyages];
      updated[selectedRowIdx].reason = tempReason;
      formik.setFieldValue("voyages", updated);
    }
    setReasonPopup(false);
    setTempReason("");
    setSelectedRowIdx(null);
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="mt-3">
        <h5 className="mb-2 fw-bold text-primary">EUA Voyage & Port Events</h5>
        <Table bordered responsive className="mb-0 align-middle text-nowrap">
          <thead className="table-light">
            <tr>
              <th style={{ width: "20%" }}>Type</th>
              <th style={{ width: "35%" }}>Reporting Events</th>
              <th style={{ width: "5%" }}>Is Cargo Operation Carried Out?</th>
              <th style={{ width: "10%" }}>Departure Port</th>
              <th style={{ width: "10%" }}>Arrival Port</th>
              <th style={{ width: "30%" }}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {formik.values.voyages.map((row, idx) => {
              const originalType = formik.values.voyages[idx].type;
              const showReason = formik.values.showReasonLink[idx];
              return (
                <tr key={idx}>
                  {/* Type Selection */}
                  <td
                    className="align-middle"
                    style={{
                      minWidth: "180px",
                      verticalAlign: "middle",
                    }}
                  >
                    <div
                      className="d-flex flex-column justify-content-center"
                      style={{
                        minHeight: "100px", // ensures stable height for all rows
                      }}
                    >
                      <div className="form-check">
                        <Input
                          type="radio"
                          id={`type_voyage_${idx}`}
                          name={`type_${idx}`}
                          value="voyage"
                          checked={row.type === "voyage"}
                          onChange={() => handleTypeChange(idx, "voyage")}
                        />
                        <Label htmlFor={`type_voyage_${idx}`} className="fw-bold ms-2">
                          Voyage
                        </Label>
                      </div>

                      <div className="form-check">
                        <Input
                          type="radio"
                          id={`type_port_${idx}`}
                          name={`type_${idx}`}
                          value="port"
                          checked={row.type === "port"}
                          onChange={() => handleTypeChange(idx, "port")}
                        />
                        <Label
                          htmlFor={`type_port_${idx}`}
                          className={`fw-bold ms-2`}
                        >
                          Port
                        </Label>
                      </div>

                      {/* Reserve space for reason link so table height doesn’t jump */}
                      <div style={{ minHeight: "20px" }}>
                        {showReason && (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleOpenReasonModal(idx);
                            }}
                            className="text-decoration-underline text-primary fw-semibold small"
                          >
                            Click here to enter reason
                          </a>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Events List */}
                  <td className="align-middle p-0">
                    <Table borderless size="sm" className="mb-0 w-100">
                      <tbody>
                        {row.events.map((e, i) => (
                          <tr key={i}>
                            <td className="align-middle p-1">
                              <div className="d-flex align-items-center justify-content-between w-100">
                                {/* Left side — Checkbox + Label */}
                                <div className="d-flex align-items-center">
                                  <Input
                                    id={`event_checkbox_${idx}_${i}`}
                                    type="checkbox"
                                    checked={e.applicable}
                                    onChange={(event) =>
                                      handleEventCheckboxChange(idx, i, event.target.checked)
                                    }
                                    className="me-2"
                                    disabled={row.type === "voyage"}
                                  />
                                  <label
                                    htmlFor={`event_checkbox_${idx}_${i}`}
                                    className={`mb-0 cursor-pointer ${originalType === "voyage" ? "opacity-50" : ""}`}
                                  >
                                    {e.name} {dateTimeFormat(e.date, false, false, true)}
                                  </label>
                                </div>

                                {/* Right side — Up/Down arrows */}
                                <div className="d-flex align-items-center gap-1">
                                  {/* Move to previous row (if first event and not first voyage) */}
                                  {i === 0 && idx > 0 && (
                                    <i
                                      className="bx bxs-up-arrow-square text-primary pointer"
                                      title="Move event to previous row"
                                      onClick={() => handleMoveUp(idx, i)}
                                    />
                                  )}

                                  {/* Move to next row (if last event and not last voyage) */}
                                  {i === row.events.length - 1 &&
                                    idx < formik.values.voyages.length - 1 && (
                                      <i
                                        className="bx bxs-down-arrow-square text-primary pointer"
                                        title="Move event to next row"
                                        onClick={() => handleMoveDown(idx, i)}
                                      />
                                    )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </td>

                  <td className="align-middle text-center">
                    <div className="square-switch sqswitch mt-1">
                      <Input
                        type="checkbox"
                        switch="none"
                        checked={row.cargo_operation}
                        disabled
                      />
                      <Label className="mt-1 opacity-50 cursor-not-allowed" />
                    </div>
                  </td>

                  <td className="align-middle text-center">{row.from_port || "-"}</td>
                  <td className="align-middle text-center">{row.to_port || "-"}</td>
                  <td className="align-middle text-start">
                    {row.reason ? (
                      <span className="text-dark">{row.reason}</span>
                    ) : (
                      <span className="text-muted">No reason given</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr>
              <td colSpan={6} className="text-end p-3">
                <Button type="submit" color="primary" className="px-4 fw-semibold">
                  Generate
                </Button>
              </td>
            </tr>
          </tfoot>
        </Table>
        <Modal
          isOpen={reasonPopup}
          toggle={() => setReasonPopup(false)}
          className="modal-dialog-centered modal-dialog-scrollable"
        >
          <ModalHeader toggle={() => setReasonPopup(false)}>Enter Reason</ModalHeader>
          <ModalBody>
            <Input
              type="textarea"
              rows={4}
              value={tempReason}
              onChange={(e) => setTempReason(e.target.value)}
              placeholder="Enter your reason here..."
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleSaveReason}>
              Save Reason
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </form>
  );
};

export default EuaVoyageTable;